import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Event as EventIcon,
  Email as EmailIcon,
  ContactMail as ContactIcon,
} from '@mui/icons-material';
import { getDashboardStats } from '../../lib/supabase';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  return (
    <Card
      sx={{
        height: '100%',
        bgcolor: '#1a1a1a',
        color: 'white',
        borderRadius: 0,
        border: '3px solid #dc143c',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: '#dc143c',
          boxShadow: '0 8px 16px rgba(220, 20, 60, 0.3)',
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: 'white',
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: '0.9rem',
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              bgcolor: '#dc143c',
              color: 'white',
              p: 1.5,
              borderRadius: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography
          variant="h2"
          component="div"
          sx={{
            fontWeight: 900,
            color: '#dc143c',
            fontFamily: '"Impact", "Arial Black", sans-serif',
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const DashboardOverview: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 4 }}>
        Error loading stats: {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Typography
        variant="h2"
        gutterBottom
        sx={{
          mb: 4,
          fontFamily: '"Impact", "Arial Black", sans-serif',
          fontWeight: 900,
          fontSize: '2.5rem',
          textTransform: 'uppercase',
          color: '#1a1a1a',
        }}
      >
        Dashboard Overview
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="Total Articles"
          value={stats?.total_articles || 0}
          icon={<ArticleIcon fontSize="large" />}
          color="#1976d2"
        />
        <StatCard
          title="Speaking Events"
          value={stats?.total_speaking_events || 0}
          icon={<EventIcon fontSize="large" />}
          color="#2e7d32"
        />
        <StatCard
          title="Newsletter Subscribers"
          value={stats?.total_newsletter_subscribers || 0}
          icon={<EmailIcon fontSize="large" />}
          color="#ed6c02"
        />
        <StatCard
          title="Contact Submissions"
          value={stats?.total_contact_submissions || 0}
          icon={<ContactIcon fontSize="large" />}
          color="#9c27b0"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
        }}
      >
        <Card sx={{ borderRadius: 0, border: '3px solid #dc143c', bgcolor: '#1a1a1a' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                fontFamily: '"Arial Black", sans-serif',
                textTransform: 'uppercase',
                color: '#dc143c',
                fontSize: '1.2rem',
              }}
            >
              Recent Activity
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Published Articles
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc143c' }}>
                  {stats?.published_articles || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Draft Articles
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc143c' }}>
                  {stats?.draft_articles || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Upcoming Events
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc143c' }}>
                  {stats?.upcoming_speaking_events || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 0, border: '3px solid #dc143c', bgcolor: '#1a1a1a' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                fontFamily: '"Arial Black", sans-serif',
                textTransform: 'uppercase',
                color: '#dc143c',
                fontSize: '1.2rem',
              }}
            >
              Engagement
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Active Subscribers
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {stats?.active_newsletter_subscribers || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Unread Contacts
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                  {stats?.unread_contact_submissions || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontFamily: '"Arial", sans-serif' }}>
                  Total Article Views
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc143c' }}>
                  {stats?.total_article_views || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardOverview;